/**
 * Reply identity — frontend mirror of functions-forms/src/ai-reply/replyIdentity.ts.
 * Used ONLY to compute the FormBuilder AI Reply tab's live preview so it matches
 * what replyQueueProcessor will actually send. Keep both in sync if either changes.
 */

const GENERIC_WORKSPACE_NAMES = new Set(['my workspace', 'workspace']);

/** Platform-owned workspace — mirror of functions-forms/src/ai-reply/replyIdentity.ts */
export const GOCTA_OWNED_WORKSPACE_ID =
  process.env.REACT_APP_GOCTA_OWNED_WORKSPACE_ID || 'yHggv6CBNsfgUhMeNWSmjFyentu1';

/**
 * Rejects blank/whitespace-only strings and anything containing "gocta"
 * (case-insensitive) — the platform brand must never appear as a client's
 * reply-from name.
 */
export function sanitizeFromName(candidate, allowPlatformBrand = false) {
  if (!candidate) return null;
  const trimmed = String(candidate).trim();
  if (!trimmed) return null;
  if (!allowPlatformBrand && /gocta/i.test(trimmed)) return null;
  return trimmed;
}

function sanitizeWorkspaceName(candidate, allowPlatformBrand = false) {
  const sanitized = sanitizeFromName(candidate, allowPlatformBrand);
  if (!sanitized) return null;
  if (GENERIC_WORKSPACE_NAMES.has(sanitized.toLowerCase())) return null;
  return sanitized;
}

export const GENERIC_FROM_NAME = 'The Team';

/**
 * Domain sender display name. Current Firestore writes `displayName`;
 * older rows may only have `fromName`. Prefer displayName, fall back to fromName.
 * Does not sanitize — pass the result into resolveFromName for the From header.
 */
export function domainSenderName(domain) {
  if (!domain) return undefined;
  const display = domain.displayName != null ? String(domain.displayName).trim() : '';
  if (display) return display;
  const legacy = domain.fromName != null ? String(domain.fromName).trim() : '';
  return legacy || undefined;
}

/**
 * Pick a workspace's best display name for stamping onto a newly created form's
 * settings.businessName: businessName → workspace name (skipping generic
 * defaults like "My Workspace") → ''. Used by FormBuilderList when creating a
 * form from a template or blank so no form is created without a brand name.
 */
export function pickWorkspaceDisplayName(workspaceBusinessName, workspaceName) {
  return sanitizeFromName(workspaceBusinessName) || sanitizeWorkspaceName(workspaceName) || '';
}

/**
 * Resolve the From display name a lead sees on a reply, in order:
 *   explicit per-form name → workspace.businessName → workspace.name
 *   (sanitized, skips generic defaults) → verified domain fromName → generic.
 */
export function resolveFromName({
  formBusinessName,
  workspaceBusinessName,
  workspaceName,
  domainFromName,
  allowPlatformBrand = false,
} = {}) {
  const allow = allowPlatformBrand === true;
  return (
    sanitizeFromName(formBusinessName, allow) ||
    sanitizeFromName(workspaceBusinessName, allow) ||
    sanitizeWorkspaceName(workspaceName, allow) ||
    sanitizeFromName(domainFromName, allow) ||
    GENERIC_FROM_NAME
  );
}
